/**
 * Signup Page
 * Organization registration with admin user creation
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { CheckCircle, Gift, AlertCircle, Loader2, ChevronDown, Check, Sparkles, Tag } from 'lucide-react'
import { api, getErrorMessage } from '../../utils/api'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../../components/ui'
import { PublicPageMeta } from '../../components/seo/PublicPageMeta'
import { listSubscriptionPlans, normalizePlanId, PUBLIC_PLAN_ORDER, type SubscriptionPlan } from '../../services/subscriptionPlans.service'
import styles from './Signup.module.css'

interface FormData {
  organisationName: string
  businessName: string
  firstName: string
  lastName: string
  email: string
  password: string
  confirmPassword: string
  agreeToTerms: boolean
}

interface FormErrors {
  organisationName?: string
  firstName?: string
  lastName?: string
  email?: string
  password?: string
  confirmPassword?: string
  agreeToTerms?: string
}

// Password strength calculation
const calculatePasswordStrength = (password: string): { score: number; text: string } => {
  let score = 0
  
  if (password.length >= 8) score++
  if (password.length >= 12) score++
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
  if (/\d/.test(password)) score++
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++

  const texts = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong']
  return { score: Math.min(score, 4), text: texts[Math.min(score, 4)] }
}

export const Signup = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, isLoading: authLoading } = useAuth()

  // Get plan from URL params
  const urlPlan = searchParams.get('plan')
  const urlBilling = searchParams.get('billing') as 'monthly' | 'yearly' | null
  const isTrial = searchParams.get('trial') === 'true'
  const cameFromPricing = searchParams.get('from') === 'pricing'

  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(true)
  const planSectionRef = useRef<HTMLDivElement>(null)

  // Plan selection state
  const [selectedPlan, setSelectedPlan] = useState<string>(() =>
    normalizePlanId(isTrial ? 'starter_free' : (urlPlan || 'starter_free'))
  )
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(
    urlBilling || 'monthly'
  )
  const [planDropdownOpen, setPlanDropdownOpen] = useState(false)

  const [formData, setFormData] = useState<FormData>({
    organisationName: '',
    businessName: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  })
  
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [globalError, setGlobalError] = useState<string | null>(null)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState('')

  // Deal code state
  const [dealCodeOpen, setDealCodeOpen] = useState(false)
  const [dealCodeInput, setDealCodeInput] = useState('')
  const [dealCodeStatus, setDealCodeStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle')
  const [dealCodeMessage, setDealCodeMessage] = useState<string>('')
  const [verifiedDealCode, setVerifiedDealCode] = useState<string | null>(null)
  const [verifiedDeal, setVerifiedDeal] = useState<{
    code: string
    percentOff?: number | null
    amountOffCents?: number | null
    discountDurationIntervals?: number | null
    targetTier?: string | null
    interval?: string | null
  } | null>(null)

  const verifyDealCode = async () => {
    const code = dealCodeInput.trim().toUpperCase()
    if (!code) return
    setDealCodeStatus('checking')
    setDealCodeMessage('')
    try {
      const res = await api.post('/subscription/deal-code/verify', { code })
      if (res.data?.data?.valid) {
        const dc = res.data.data.dealCode ?? res.data.data.deal
        setDealCodeStatus('valid')
        setVerifiedDealCode(code)
        setVerifiedDeal(dc ?? null)
        const parts: string[] = []
        if (dc?.percentOff) parts.push(`${dc.percentOff}% off`)
        else if (dc?.amountOffCents) parts.push(`$${(dc.amountOffCents / 100).toFixed(0)} off`)
        if (dc?.discountDurationIntervals) parts.push(`for ${dc.discountDurationIntervals} months`)
        else parts.push('ongoing')
        setDealCodeMessage(parts.join(' '))

        // Auto-select the target plan if the deal code specifies one
        if (dc?.targetTier) {
          const normalized = normalizePlanId(dc.targetTier)
          setSelectedPlan(normalized)
        }
        // Auto-set billing interval if the deal code specifies one
        if (dc?.interval && dc.interval !== 'any') {
          setBillingCycle(dc.interval as 'monthly' | 'yearly')
        }
      } else {
        setDealCodeStatus('invalid')
        setVerifiedDealCode(null)
        setVerifiedDeal(null)
        setDealCodeMessage(res.data?.data?.message || 'Invalid or expired deal code')
      }
    } catch {
      setDealCodeStatus('invalid')
      setVerifiedDealCode(null)
      setVerifiedDeal(null)
      setDealCodeMessage('Unable to verify code — please try again')
    }
  }

  // Load plans from backend (single source of truth)
  useEffect(() => {
    let isMounted = true

    const loadPlans = async () => {
      try {
        setPlansLoading(true)
        const fetched = await listSubscriptionPlans()
        const sorted = [...(fetched || [])].sort(
          (a: SubscriptionPlan, b: SubscriptionPlan) =>
            (PUBLIC_PLAN_ORDER[normalizePlanId(a.id)] ?? 999) - (PUBLIC_PLAN_ORDER[normalizePlanId(b.id)] ?? 999)
        )

        if (!isMounted) return
        setPlans(sorted)

        // If the selected plan isn't present (e.g. old URL param), fall back safely.
        const normalizedSelected = normalizePlanId(selectedPlan)
        const hasSelected = sorted.some((p: SubscriptionPlan) => normalizePlanId(p.id) === normalizedSelected)
        if (!hasSelected) {
          setSelectedPlan(normalizePlanId(sorted[0]?.id || 'starter_free'))
        } else {
          // Ensure we always store canonical IDs in state.
          setSelectedPlan(normalizedSelected)
        }
      } catch (e) {
        if (!isMounted) return
        setPlans([])
      } finally {
        if (!isMounted) return
        setPlansLoading(false)
      }
    }

    loadPlans()
    return () => {
      isMounted = false
    }
  }, [])

  // Get selected plan details
  const selectedPlanDetails = useMemo(() => {
    const canonicalSelected = normalizePlanId(selectedPlan)
    const found = plans.find((p: SubscriptionPlan) => normalizePlanId(p.id) === canonicalSelected)

    return (
      found || {
        id: 'starter_free',
        name: 'Starter (Free)',
        description: 'No time limit — explore TCGEasy before choosing a paid plan',
        price: 0,
        yearlyPrice: 0,
        maxUsers: 1,
        maxProducts: 100,
        maxApiCalls: 500,
        maxPlatforms: 1,
        maxStorageGB: 0.5,
        qrCodeAccess: 'none',
        features: ['Browse the card catalogue and pricing', 'View-only inventory (adding cards needs a paid plan)', 'No credit card required'],
        purchasable: false,
      }
    )
  }, [selectedPlan, plans])

  // Calculate savings percentage for yearly billing
  const getSavingsPercentage = (plan: SubscriptionPlan): number => {
    const monthly = plan.price || 0
    const yearly = plan.yearlyPrice || 0
    if (monthly <= 0 || yearly <= 0) return 0
    const yearlyMonthly = yearly / 12
    const savings = ((monthly - yearlyMonthly) / monthly) * 100
    return Math.round(savings)
  }

  // Format price display
  const formatPrice = (plan: SubscriptionPlan): string => {
    const monthly = plan.price || 0
    const yearly = plan.yearlyPrice || 0

    if (monthly <= 0) return 'Free'

    if (billingCycle === 'yearly') {
      const effectiveYearly = yearly > 0 ? yearly : monthly * 12
      const monthlyEquivalent = Math.round(effectiveYearly / 12)
      return `$${monthlyEquivalent}/mo`
    }

    return `$${monthly}/mo`
  }

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, authLoading, navigate])

  // Password strength
  const passwordStrength = useMemo(() => {
    return calculatePasswordStrength(formData.password)
  }, [formData.password])

  /**
   * Handle input changes
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Clear specific error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
    if (globalError) setGlobalError(null)
  }

  /**
   * Validate form
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Organisation name
    if (!formData.organisationName.trim()) {
      newErrors.organisationName = 'Organisation name is required'
    } else if (formData.organisationName.length < 2) {
      newErrors.organisationName = 'Organisation name must be at least 2 characters'
    }

    // First name
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }

    // Last name
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }

    // Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Password
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter'
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter'
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number'
    }

    // Confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    // Terms
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setGlobalError(null)

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      // Register the organization and user
      const response = await api.post('/auth/register', {
        organisationName: formData.organisationName.trim(),
        businessName: formData.businessName.trim() || undefined,
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        // Include selected plan info
        selectedPlan: selectedPlan,
        billingCycle: billingCycle,
        ...(verifiedDealCode ? { dealCode: verifiedDealCode } : {})
      })

      if (response.data.success) {
        setRegisteredEmail(formData.email.trim().toLowerCase())

        setRegistrationSuccess(true)
      }
    } catch (err: any) {
      const errorMessage = getErrorMessage(err)
      setGlobalError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <h1 className={styles.title}>TCGEasy</h1>
            <p className={styles.subtitle}>Loading...</p>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (registrationSuccess) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successContainer}>
            <div className={styles.successIcon}>
              <CheckCircle size={40} />
            </div>
            <h1 className={styles.successTitle}>Welcome to TCGEasy!</h1>
            <p className={styles.successText}>
              We've sent a verification email to{' '}
              <span className={styles.successEmail}>{registeredEmail}</span>.
              <br />
              Please check your inbox and click the verification link to activate your account.
            </p>
            <Button
              variant="primary"
              size="large"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </div>
          <div className={styles.footer}>
            <p className={styles.footerText}>
              Didn't receive the email? Check your spam folder or contact support.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <PublicPageMeta
        title="Create your account"
        description="Register your organisation and start managing TCG inventory, pricing, and platform sync with TCGEasy."
      />
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          {cameFromPricing && (
            <div className={styles.headerNavRow}>
              <Link to="/pricing" className={styles.backToPricingLink}>
                ← Back to Pricing
              </Link>
            </div>
          )}
          <h1 className={styles.title}>Create Your Account</h1>
          <p className={styles.subtitle}>
            Start managing your TCG inventory with ease
          </p>
        </div>

        {/* Plan Selection Section */}
        <div className={styles.planSection} ref={planSectionRef}>
          <div className={styles.sectionTitle}>Choose Your Plan</div>
          
          {/* Billing Toggle */}
          <div className={styles.billingToggle}>
            <button
              type="button"
              className={`${styles.billingOption} ${billingCycle === 'monthly' ? styles.billingActive : ''}`}
              onClick={() => setBillingCycle('monthly')}
              disabled={!!(verifiedDeal?.interval && verifiedDeal.interval !== 'any')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`${styles.billingOption} ${billingCycle === 'yearly' ? styles.billingActive : ''}`}
              onClick={() => setBillingCycle('yearly')}
              disabled={!!(verifiedDeal?.interval && verifiedDeal.interval !== 'any')}
            >
              Yearly
              <span className={styles.saveBadge}>Save 17%</span>
            </button>
          </div>

          {/* Plan Dropdown */}
          <div className={styles.planDropdownContainer}>
            <button
              type="button"
              className={`${styles.planDropdownTrigger} ${planDropdownOpen ? styles.planDropdownOpen : ''}`}
              onClick={() => setPlanDropdownOpen(!planDropdownOpen)}
              disabled={plansLoading || !!(verifiedDeal?.targetTier)}
            >
              <div className={styles.selectedPlanInfo}>
                <div className={styles.selectedPlanHeader}>
                  <span className={styles.selectedPlanName}>
                    {selectedPlanDetails.name}
                    {normalizePlanId(selectedPlanDetails.id) === 'professional' && <Sparkles size={14} className={styles.popularIcon} />}
                  </span>
                  <span className={styles.selectedPlanPrice}>
                    {formatPrice(selectedPlanDetails)}
                    {normalizePlanId(selectedPlanDetails.id) === 'starter_free' && (
                      <span className={styles.trialNote}>No time limit</span>
                    )}
                    {billingCycle === 'yearly' && (selectedPlanDetails.price || 0) > 0 && (
                      <span className={styles.savingsNote}>
                        Save {getSavingsPercentage(selectedPlanDetails)}%
                      </span>
                    )}
                  </span>
                </div>
                <span className={styles.selectedPlanDesc}>{selectedPlanDetails.description}</span>
              </div>
              <ChevronDown size={20} className={`${styles.dropdownIcon} ${planDropdownOpen ? styles.dropdownIconOpen : ''}`} />
            </button>

            {planDropdownOpen && (
              <div className={styles.planDropdownMenu}>
                {(plans.length ? plans : [selectedPlanDetails]).map((plan: SubscriptionPlan) => (
                  <button
                    key={plan.id}
                    type="button"
                    className={`${styles.planOption} ${normalizePlanId(selectedPlan) === normalizePlanId(plan.id) ? styles.planOptionSelected : ''} ${normalizePlanId(plan.id) === 'professional' ? styles.planOptionPopular : ''}`}
                    onClick={() => {
                      setSelectedPlan(normalizePlanId(plan.id))
                      setPlanDropdownOpen(false)
                    }}
                    disabled={plansLoading}
                  >
                    <div className={styles.planOptionContent}>
                      <div className={styles.planOptionHeader}>
                        <span className={styles.planOptionName}>
                          {plan.name}
                          {normalizePlanId(plan.id) === 'professional' && <span className={styles.popularBadge}>Popular</span>}
                        </span>
                        <span className={styles.planOptionPrice}>
                          {(plan.price || 0) <= 0 ? (
                            'Free'
                          ) : billingCycle === 'yearly' ? (
                            <>
                              ${Math.round(((plan.yearlyPrice || (plan.price || 0) * 12) || 0) / 12)}/mo
                              <span className={styles.yearlyTotal}>${plan.yearlyPrice || (plan.price || 0) * 12}/year</span>
                            </>
                          ) : (
                            `$${plan.price}/mo`
                          )}
                        </span>
                      </div>
                      <span className={styles.planOptionDesc}>{plan.description}</span>
                      <div className={styles.planOptionFeatures}>
                        {plan.features.slice(0, 3).map((feature, idx) => (
                          <span key={idx} className={styles.planFeature}>
                            <Check size={12} /> {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                    {normalizePlanId(selectedPlan) === normalizePlanId(plan.id) && (
                      <div className={styles.selectedCheck}>
                        <CheckCircle size={20} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Plan Summary */}
          {normalizePlanId(selectedPlanDetails.id) !== 'starter_free' && (
            <div className={styles.priceSummary}>
              <div className={styles.priceRow}>
                <span>Plan</span>
                <span>{selectedPlanDetails.name}</span>
              </div>
              <div className={styles.priceRow}>
                <span>Billing</span>
                <span>{billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</span>
              </div>
              <div className={`${styles.priceRow} ${styles.priceTotal}`}>
                <span>Total</span>
                <span>
                  {(() => {
                    const monthly = selectedPlanDetails.price || 0
                    const yearly = selectedPlanDetails.yearlyPrice || monthly * 12

                    return billingCycle === 'yearly'
                      ? `$${yearly} AUD/year`
                      : `$${monthly} AUD/month`
                  })()}
                </span>
              </div>
              {billingCycle === 'yearly' && (
                <div className={styles.savingsRow}>
                  You save ${(selectedPlanDetails.price || 0) * 12 - (selectedPlanDetails.yearlyPrice || (selectedPlanDetails.price || 0) * 12)} AUD per year!
                </div>
              )}
            </div>
          )}

          {normalizePlanId(selectedPlanDetails.id) === 'starter_free' && (
            <div className={styles.trialInfo}>
              <Gift size={16} />
              <span>No credit card required</span>
              <span>
                If you connect Shopify later, you can upgrade to a paid plan. You approve the subscription in Shopify; billing starts when you choose a paid plan.
              </span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {globalError && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              <AlertCircle className={styles.alertIcon} />
              <span>{globalError}</span>
            </div>
          )}

          {/* Organisation Section */}
          <div className={styles.sectionTitle}>Organisation Details</div>
          
          <div className={styles.formGroup}>
            <label htmlFor="organisationName" className={styles.label}>
              Organisation Name
            </label>
            <input
              id="organisationName"
              name="organisationName"
              type="text"
              value={formData.organisationName}
              onChange={handleChange}
              placeholder="My TCG Store"
              disabled={isSubmitting}
              className={`${styles.input} ${errors.organisationName ? styles.inputError : ''}`}
            />
            {errors.organisationName && (
              <span className={styles.inputErrorText}>{errors.organisationName}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="businessName" className={styles.label}>
              Business Name <span className={styles.labelOptional}>(optional)</span>
            </label>
            <input
              id="businessName"
              name="businessName"
              type="text"
              value={formData.businessName}
              onChange={handleChange}
              placeholder="Legal business name for invoicing"
              disabled={isSubmitting}
              className={styles.input}
            />
            <span className={styles.inputHint}>
              Leave blank to use organisation name for invoices
            </span>
          </div>

          {/* Admin User Section */}
          <div className={styles.sectionTitle}>Admin Account</div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="firstName" className={styles.label}>
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="John"
                disabled={isSubmitting}
                className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
              />
              {errors.firstName && (
                <span className={styles.inputErrorText}>{errors.firstName}</span>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="lastName" className={styles.label}>
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Smith"
                disabled={isSubmitting}
                className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
              />
              {errors.lastName && (
                <span className={styles.inputErrorText}>{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              disabled={isSubmitting}
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
            />
            {errors.email && (
              <span className={styles.inputErrorText}>{errors.email}</span>
            )}
            <span className={styles.inputHint}>
              This will be your login email and receive important updates
            </span>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a secure password"
              disabled={isSubmitting}
              className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
            />
            {formData.password && (
              <>
                <div className={styles.passwordStrength}>
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`${styles.strengthBar} ${
                        i < passwordStrength.score
                          ? passwordStrength.score <= 1
                            ? styles.weak
                            : passwordStrength.score === 2
                            ? styles.fair
                            : passwordStrength.score === 3
                            ? styles.good
                            : styles.strong
                          : ''
                      }`}
                    />
                  ))}
                </div>
                <span className={styles.strengthText}>
                  Password strength: {passwordStrength.text}
                </span>
              </>
            )}
            {errors.password && (
              <span className={styles.inputErrorText}>{errors.password}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              disabled={isSubmitting}
              className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
            />
            {formData.confirmPassword && !errors.confirmPassword && (
              formData.password === formData.confirmPassword ? (
                <span className={styles.inputHint} style={{ color: 'var(--color-success, #22c55e)' }}>
                  <CheckCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                  Passwords match
                </span>
              ) : (
                <span className={styles.inputErrorText}>Passwords do not match</span>
              )
            )}
            {errors.confirmPassword && (
              <span className={styles.inputErrorText}>{errors.confirmPassword}</span>
            )}
          </div>

          {/* Deal code */}
          <div className={styles.dealCodeSection}>
            <button
              type="button"
              className={styles.dealCodeToggle}
              onClick={() => setDealCodeOpen(o => !o)}
            >
              <Tag size={15} />
              Have a deal code?
              <ChevronDown size={14} style={{ marginLeft: 'auto', transform: dealCodeOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>
            {dealCodeOpen && (
              <div className={styles.dealCodeBody}>
                {dealCodeStatus === 'valid' ? (
                  <div className={styles.dealCodeValid}>
                    <CheckCircle size={16} />
                    <span><strong>{verifiedDealCode}</strong> — {dealCodeMessage}</span>
                    {verifiedDeal?.targetTier && (
                      <span className={styles.dealCodePlanNote}>
                        Plan auto-selected: {plans.find(p => normalizePlanId(p.id) === normalizePlanId(verifiedDeal.targetTier!))?.name || verifiedDeal.targetTier}
                      </span>
                    )}
                    <div className={styles.dealCodeActions}>
                      {verifiedDeal?.targetTier && (
                        <button
                          type="button"
                          className={styles.dealCodeClaimBtn}
                          onClick={() => {
                            planSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                          }}
                        >
                          <Gift size={14} />
                          Claim Now
                        </button>
                      )}
                      <button type="button" className={styles.dealCodeRemove} onClick={() => { setDealCodeStatus('idle'); setVerifiedDealCode(null); setVerifiedDeal(null); setDealCodeInput(''); setDealCodeMessage('') }}>Remove</button>
                    </div>
                  </div>
                ) : (
                  <div className={styles.dealCodeRow}>
                    <input
                      type="text"
                      className={styles.dealCodeInput}
                      placeholder="e.g. XMAS2024"
                      value={dealCodeInput}
                      onChange={e => { setDealCodeInput(e.target.value.toUpperCase()); setDealCodeStatus('idle'); setDealCodeMessage('') }}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), verifyDealCode())}
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      className={styles.dealCodeVerifyBtn}
                      onClick={verifyDealCode}
                      disabled={!dealCodeInput.trim() || dealCodeStatus === 'checking' || isSubmitting}
                    >
                      {dealCodeStatus === 'checking' ? <Loader2 size={14} className="animate-spin" /> : 'Apply'}
                    </button>
                  </div>
                )}
                {dealCodeStatus === 'invalid' && (
                  <span className={styles.dealCodeError}>{dealCodeMessage}</span>
                )}
              </div>
            )}
          </div>

          {/* Terms and Conditions */}
          <label className={styles.termsLabel}>
            <input
              type="checkbox"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleChange}
              disabled={isSubmitting}
              className={styles.checkbox}
            />
            <span>
              I agree to the{' '}
              <Link to="/tos" className={styles.termsLink}>Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className={styles.termsLink}>Privacy Policy</Link>
            </span>
          </label>
          {errors.agreeToTerms && (
            <span className={styles.inputErrorText}>{errors.agreeToTerms}</span>
          )}

          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            isLoading={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Creating your account...
              </>
            ) : normalizePlanId(selectedPlan) === 'starter_free' ? (
              'Create Free Account'
            ) : (
              `Continue to Payment - ${formatPrice(selectedPlanDetails)}`
            )}
          </Button>

          {normalizePlanId(selectedPlan) !== 'starter_free' && (
            <p className={styles.paymentNote}>
              You'll be redirected to our secure payment page after creating your account.
            </p>
          )}
        </form>

        <div className={styles.loginPrompt}>
          Already have an account?
          <Link to="/login" className={styles.loginLink}>Sign in</Link>
        </div>
      </div>
    </div>
    </>
  )
}

export default Signup
